/**
 * Therapy session pages:
 *   /therapy/intake/:id   — couples intake form (gates session join)
 *   /therapy/session/:id  — JaaS embed with branded UI
 *   /therapy/feedback/:id — post-session 1-5 rating
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, Heart, ArrowLeft, CheckCircle2, Star, Loader2, CreditCard, Sparkles,
  X,
} from "lucide-react";
import Seo from "../components/Seo";
import { LiveStage } from "../components/live/LiveStage";
import { useAuth } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ============== INTAKE ============== */
export function TherapyIntakePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [form, setForm] = useState({
    your_name: "", partner_name: "", relationship_length: "",
    primary_concern: "", goals: "", prior_therapy: false, safety_concerns: false,
    consent_to_treatment: false, consent_to_eparticipation: false, additional: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/therapy/sessions/${id}`, { withCredentials: true })
      .then((r) => {
        setS(r.data);
        setForm((f) => ({ ...f, your_name: user?.name || "" }));
      })
      .catch(() => toast.error("Session not found"));
  }, [id, user]);

  async function submit() {
    if (!form.consent_to_treatment || !form.consent_to_eparticipation) {
      return toast.error("Both consent boxes are required.");
    }
    if (!form.your_name || !form.partner_name || !form.primary_concern) {
      return toast.error("Please fill all required fields.");
    }
    setBusy(true);
    try {
      await axios.post(`${API}/therapy/intake`, { session_id: id, ...form }, { withCredentials: true });
      toast.success("Intake submitted.");
      nav(`/therapy/session/${id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  }

  if (!s) return <div className="min-h-screen flex items-center justify-center text-sm text-black/40">Loading…</div>;

  if (s.intake_completed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <p className="text-lg font-serif">Intake already submitted.</p>
          <Link to={`/therapy/session/${id}`} className="btn-gold mt-4 inline-block">Go to session</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="therapy-intake-page">
      <Seo title="Couples Intake Form" path={`/therapy/intake/${id}`} noindex />
      <div className="container-wide max-w-2xl mx-auto px-6 py-12">
        <Link to="/dashboard/therapy" className="text-sm text-black/50 flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3" /> My sessions</Link>
        <div className="mb-6">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)]">Confidential</p>
          <h1 className="font-serif text-4xl mt-1">Couples Therapy Intake</h1>
          <p className="text-sm text-black/60 mt-2">
            Your therapist will see this. Encrypted at rest. We'll auto-attach it to your therapist's session notes.
          </p>
        </div>

        <div className="bg-white border border-[var(--line)] rounded-2xl p-8 space-y-4">
          <Inp label="Your name *" v={form.your_name} on={(v) => setForm({ ...form, your_name: v })} testid="intake-your-name" />
          <Inp label="Partner's name *" v={form.partner_name} on={(v) => setForm({ ...form, partner_name: v })} testid="intake-partner-name" />
          <Inp label="Relationship length" v={form.relationship_length} on={(v) => setForm({ ...form, relationship_length: v })} placeholder="e.g. 3 years" testid="intake-rel-length" />
          <Area label="Primary concern *" v={form.primary_concern} on={(v) => setForm({ ...form, primary_concern: v })} testid="intake-concern" />
          <Area label="Goals for therapy" v={form.goals} on={(v) => setForm({ ...form, goals: v })} testid="intake-goals" />
          <div className="grid sm:grid-cols-2 gap-3">
            <Chk label="Either of us has been in therapy before" v={form.prior_therapy} on={(v) => setForm({ ...form, prior_therapy: v })} testid="intake-prior" />
            <Chk label="Any safety / abuse concerns" v={form.safety_concerns} on={(v) => setForm({ ...form, safety_concerns: v })} testid="intake-safety" />
          </div>
          <Area label="Anything else" v={form.additional} on={(v) => setForm({ ...form, additional: v })} testid="intake-additional" />

          <div className="bg-black/5 rounded-xl p-5 mt-4 space-y-2">
            <p className="text-[11px] uppercase tracking-widest text-black/50 mb-2">Required consents</p>
            <Chk label="I consent to treatment by the licensed therapist" v={form.consent_to_treatment} on={(v) => setForm({ ...form, consent_to_treatment: v })} testid="intake-consent-treatment" />
            <Chk label="I consent to electronic (video) participation and the platform's terms" v={form.consent_to_eparticipation} on={(v) => setForm({ ...form, consent_to_eparticipation: v })} testid="intake-consent-eparticipation" />
          </div>

          <button onClick={submit} disabled={busy} className="btn-gold w-full" data-testid="intake-submit">
            <ShieldCheck className="w-4 h-4" /> {busy ? "Submitting…" : "Submit intake & continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Inp({ label, v, on, testid, placeholder = "" }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-black/50">{label}</label>
      <input value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder}
        className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
        data-testid={testid} />
    </div>
  );
}
function Area({ label, v, on, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-black/50">{label}</label>
      <textarea value={v} onChange={(e) => on(e.target.value)} rows={3}
        className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
        data-testid={testid} />
    </div>
  );
}
function Chk({ label, v, on, testid }) {
  return (
    <label className="flex items-start gap-2 text-sm cursor-pointer py-1" data-testid={testid}>
      <input type="checkbox" checked={!!v} onChange={(e) => on(e.target.checked)} className="mt-1 accent-[var(--gold)]" />
      <span>{label}</span>
    </label>
  );
}

/* ============== SESSION ROOM (JaaS embed) ============== */
export function TherapySessionRoomPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [step, setStep] = useState("loading"); // loading | gate | starting | live | done | error
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    axios.get(`${API}/therapy/settings`).then((r) => setSettings(r.data || {})).catch(() => null);
  }, []);

  async function load() {
    try {
      const r = await axios.get(`${API}/therapy/sessions/${id}`, { withCredentials: true });
      setS(r.data);
      // If client + payment param triggered, poll status
      if (params.get("paid") === "1") {
        await axios.get(`${API}/therapy/sessions/${id}/payment-status`, { withCredentials: true });
        const r2 = await axios.get(`${API}/therapy/sessions/${id}`, { withCredentials: true });
        setS(r2.data);
      }
      setStep("gate");
    } catch (e) {
      setError("Session not found"); setStep("error");
    }
  }
  useEffect(() => { if (user) load(); /* eslint-disable-line */ }, [user]);

  const [join, setJoin] = useState(null);
  async function joinNow() {
    setStep("starting");
    try {
      const r = await axios.post(`${API}/therapy/sessions/${id}/join`, {}, { withCredentials: true });
      setJoin(r.data); setStep("live");
    } catch (e) {
      setError(e?.response?.data?.detail || "Join failed"); setStep("error");
    }
  }

  function leaveRoom() {
    setJoin(null);
    if (user.user_id === s.client_user_id) nav(`/therapy/feedback/${id}`);
    else if (user.user_id === s.therapist_user_id) nav(`/dashboard/therapy/notes/${id}`);
    else nav("/dashboard");
  }

  async function payNow() {
    setPaying(true);
    try {
      const r = await axios.post(`${API}/therapy/sessions/${id}/checkout`, {}, {
        withCredentials: true,
        headers: { Origin: window.location.origin },
      });
      if (r.data.already_paid) { await load(); return; }
      window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); }
    finally { setPaying(false); }
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Link to={`/login?next=/therapy/session/${id}`} className="btn-gold">Log in to join session</Link>
      </div>
    );
  }
  if (step === "loading") return <div className="min-h-screen flex items-center justify-center text-sm text-black/40">Loading…</div>;
  if (step === "error") {

    return (
      <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center p-6" data-testid="therapy-session-error">
        <div className="bg-white border border-red-200 rounded-2xl p-8 max-w-lg w-full text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 mx-auto flex items-center justify-center mb-4">
            <X className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="font-serif text-2xl mb-2">Couldn't connect</h2>
          <p className="text-sm text-black/70 leading-relaxed">{error}</p>
          <div className="flex gap-2 justify-center mt-6 flex-wrap">
            <button onClick={() => { setError(""); setStep("gate"); }}
              className="text-sm px-4 py-2 rounded-xl border border-[var(--line)] hover:bg-[#C8A96B]/10"
              data-testid="error-retry">
              Retry
            </button>
            <Link to="/dashboard/therapy" className="text-sm px-4 py-2 rounded-xl border border-[var(--line)] hover:bg-[#C8A96B]/10">
              Back to sessions
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isClient = s && user.user_id === s.client_user_id;
  const isHost = s && (user.user_id === s.therapist_user_id || user.role === "admin");
  const needsPay = isClient && !isHost && !["paid", "complimentary"].includes(s?.payment_status);
  const needsIntake = isClient && !isHost && !s?.intake_completed;

  if (step === "gate") {
    return (
      <div className="min-h-screen bg-[var(--bg)]" data-testid="therapy-session-gate">
        <Seo title="Session room" path={`/therapy/session/${id}`} noindex />
        <div className="container-wide max-w-2xl mx-auto px-6 py-12">
          <h1 className="font-serif text-4xl mb-2">{isHost ? "Start therapy session" : "Therapy session"}</h1>
          <p className="text-sm text-black/60 mb-6">
            {isHost
              ? <>Your client: <strong>{s?.client_name || s?.client_email}</strong> · {(s?.starts_at || "").slice(0, 16).replace("T", " ")} UTC · {s?.duration_minutes} min</>
              : <>{s?.therapist_name} · {(s?.starts_at || "").slice(0, 16).replace("T", " ")} UTC · {s?.duration_minutes} min</>
            }
          </p>

          <div className="bg-white border border-[var(--line)] rounded-2xl p-8 space-y-4">
            <Check label="Logged in" ok={!!user} testid="gate-login" />
            {isClient && !isHost && <Check label="Payment complete" ok={!needsPay} testid="gate-payment" />}
            {isClient && !isHost && <Check label="Intake form complete" ok={!needsIntake} testid="gate-intake" />}
            <div className="pt-4 border-t border-[var(--line)] flex flex-wrap gap-3">
              {needsPay && (
                <button onClick={payNow} disabled={paying} className="btn-gold flex-1" data-testid="gate-pay">
                  <CreditCard className="w-4 h-4" /> Pay ${s?.amount?.toFixed(2)} & continue
                </button>
              )}
              {!needsPay && needsIntake && (
                <Link to={`/therapy/intake/${id}`} className="btn-gold flex-1" data-testid="gate-intake-link">
                  <Sparkles className="w-4 h-4" /> Complete intake
                </Link>
              )}
              {!needsPay && !needsIntake && (
                <button onClick={joinNow} className="btn-gold flex-1" data-testid="gate-join">
                  <Heart className="w-4 h-4" /> {isHost ? "Start session" : "Enter session"}
                </button>
              )}
            </div>

            {isHost && (
              <p className="text-xs text-black/40 mt-2">
                As host, you can start the room any time — your client joins via their dashboard or the magic link.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-50" data-testid="therapy-session-room">
      {join ? <LiveStage zoom join={join} title={isHost ? `Session with ${s?.client_name || "your client"}` : `Session with ${s?.therapist_name || "your therapist"}`} onLeave={leaveRoom} />
        : <div className="absolute inset-0 flex items-center justify-center text-white"><Loader2 className="w-10 h-10 animate-spin" /></div>}
    </div>
  );
}

function Check({ label, ok, testid }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      {ok ? (
        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
      ) : (
        <div className="w-5 h-5 rounded-full border-2 border-amber-400" />
      )}
      <span className="text-sm">{label}</span>
    </div>
  );
}

/* ============== POST-SESSION FEEDBACK ============== */
export function TherapyFeedbackPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);

  async function submit() {
    if (!rating) return toast.error("Please pick 1–5 stars");
    try {
      await axios.post(`${API}/therapy/sessions/${id}/feedback`, { rating, comment }, { withCredentials: true });
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center" data-testid="feedback-done">
          <Heart className="w-12 h-12 text-[var(--gold)] mx-auto mb-3" />
          <p className="font-serif text-3xl">Thank you</p>
          <p className="text-sm text-black/60 mt-2">Your feedback helps couples find the right therapist.</p>
          <button onClick={() => nav("/dashboard/therapy")} className="btn-gold mt-6">My sessions</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="therapy-feedback-page">
      <Seo title="Rate your session" path={`/therapy/feedback/${id}`} noindex />
      <div className="container-wide max-w-xl mx-auto px-6 py-12 text-center">
        <h1 className="font-serif text-4xl mb-2">How was the session?</h1>
        <p className="text-sm text-black/60 mb-8">Anonymous to other users; only your therapist + admin see it.</p>
        <div className="flex justify-center gap-2 mb-6">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setRating(n)}
              className={n <= rating ? "text-[var(--gold)]" : "text-black/20"}
              data-testid={`feedback-star-${n}`}>
              <Star className="w-10 h-10 fill-current" />
            </button>
          ))}
        </div>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4}
          placeholder="Anything you'd like us to know? (optional)"
          className="w-full px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)] mb-4"
          data-testid="feedback-comment" />
        <button onClick={submit} className="btn-gold" data-testid="feedback-submit">Submit feedback</button>
      </div>
    </div>
  );
}
